import { useEffect, useState } from "react";
import { Copy, Link as LinkIcon, Trash2, Shield, X } from "lucide-react";
import { toast } from "sonner";
import {
  createShare, listShares, revokeShare, buildShareUrl,
} from "@/lib/api";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function ShareDialog({ resumeId, onClose }) {
  const [shares, setShares] = useState([]);
  const [days, setDays] = useState(7);
  const [anonymize, setAnonymize] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = () =>
    listShares(resumeId)
      .then(setShares)
      .catch(() => toast.error("Could not load share links"))
      .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
     
  }, [resumeId]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const info = await createShare(resumeId, { days, anonymize });
      const url = buildShareUrl(info.token);
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Link created & copied");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (token) => {
    const url = buildShareUrl(token);
    navigator.clipboard?.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const handleRevoke = async (token) => {
    if (!window.confirm("Revoke this link? The recipient won't be able to access it.")) return;
    try {
      await revokeShare(token);
      toast.success("Link revoked");
      refresh();
    } catch {
      toast.error("Revoke failed");
    }
  };

  const isActive = (s) => {
    if (s.revoked) return false;
    try { return new Date(s.expires_at) > new Date(); } catch { return false; }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 fade-in"
      onClick={onClose}
      data-testid="share-dialog-overlay"
    >
      <div
        className="bg-white border border-[var(--border-default)] hard-shadow-strong w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-dialog"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-default)]">
          <div>
            <div className="overline">share · public link</div>
            <h2 className="font-heading text-xl font-bold tracking-tight text-[var(--text-primary)] mt-1">
              SHARE THIS RESUME
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost"
            data-testid="share-dialog-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          <div className="bg-[var(--bg-tint)] border border-[var(--border-default)] p-4">
            <div className="flex items-start gap-3 mb-3">
              <Shield className="w-5 h-5 text-[var(--brand-primary)] mt-0.5" strokeWidth={1.5} />
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                Generate an unguessable, time-limited link to share this resume
                with hiring managers <strong>without requiring a login</strong>.
                You can revoke any link at any time.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end mt-3">
              <div>
                <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1.5">
                  Link valid for
                </label>
                <select
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  className="swiss-input"
                  data-testid="share-days-select"
                >
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days (recommended)</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={anonymize}
                    onChange={(e) => setAnonymize(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="share-anonymize-toggle"
                  />
                  Force anonymize
                </label>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--text-muted)] mt-1">
                  blind-hire mode
                </p>
              </div>
            </div>

            <button
              onClick={handleCreate}
              disabled={creating}
              className="btn-primary w-full sm:w-auto mt-4"
              data-testid="create-share-button"
            >
              {creating ? <span className="spinner" /> : <LinkIcon className="w-4 h-4" strokeWidth={1.5} />}
              CREATE SHARE LINK
            </button>
          </div>

          {/* Existing links */}
          <div>
            <div className="overline mb-3">existing links</div>
            {loading && (
              <div className="text-sm text-[var(--text-muted)]" data-testid="shares-loading">
                Loading…
              </div>
            )}
            {!loading && shares.length === 0 && (
              <div className="text-sm text-[var(--text-muted)]" data-testid="shares-empty">
                No share links yet. Generate one above to send this profile to a hiring manager.
              </div>
            )}
            <ul className="space-y-2" data-testid="shares-list">
              {shares.map((s) => {
                const active = isActive(s);
                return (
                  <li
                    key={s.token}
                    className={`flex flex-wrap items-center justify-between gap-3 p-3 border ${
                      active
                        ? "border-[var(--brand-primary)]/30 bg-[var(--brand-primary)]/[0.04]"
                        : "border-[var(--border-default)] bg-[var(--bg-muted)]/40"
                    }`}
                    data-testid={`share-row-${s.token}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-[var(--text-primary)] truncate">
                        {buildShareUrl(s.token)}
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--text-muted)] mt-1">
                        Created {fmtDate(s.created_at)} · Expires {fmtDate(s.expires_at)}
                        {s.anonymize && " · anonymized"}
                        {!active && (s.revoked ? " · REVOKED" : " · EXPIRED")}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {active && (
                        <button
                          onClick={() => copyLink(s.token)}
                          className="btn-secondary !py-1.5 !px-2.5 text-xs"
                          data-testid={`copy-share-${s.token}`}
                          title="Copy link"
                        >
                          <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                          Copy
                        </button>
                      )}
                      {active && (
                        <button
                          onClick={() => handleRevoke(s.token)}
                          className="btn-ghost !py-1.5 !px-2 text-xs hover:text-[var(--brand-danger)]"
                          data-testid={`revoke-share-${s.token}`}
                          title="Revoke link"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
